/** Persistent, renderer-agnostic Observatory construction data. */
export type HouseModuleZone = 'interior' | 'yard' | 'either';

export interface HouseModuleVisual {
  primitive: 'rug' | 'seat' | 'shelf' | 'table' | 'planter' | 'statue' | 'divider' | 'lamp';
  color: number;
  accent: number;
  /** Reserved drop-in path for a future free/CC0 premium GLTF replacement. */
  gltf?: string;
}

export interface HouseModuleDef {
  id: string;
  name: string;
  description: string;
  cost: number;
  zone: HouseModuleZone;
  width: number;
  depth: number;
  solid: boolean;
  visual: HouseModuleVisual;
}

export interface HousePlacement {
  id: string;
  moduleId: string;
  x: number;
  z: number;
  /** Radians, intentionally not a quarter-turn enum so finer rotation can ship later. */
  rotation: number;
}

export const HOUSE_MODULES: readonly HouseModuleDef[] = [
  { id: 'astral_rug', name: 'Astral Rug', description: 'A soft constellation mosaic for the main room.', cost: 45, zone: 'interior', width: 3, depth: 2, solid: false, visual: { primitive: 'rug', color: 0x31506a, accent: 0x7deeff } },
  { id: 'timekeeper_settee', name: 'Timekeeper Settee', description: 'A compact seat built around a recovered era frame.', cost: 80, zone: 'interior', width: 3, depth: 1.5, solid: true, visual: { primitive: 'seat', color: 0x5d466d, accent: 0xd2a6ff } },
  { id: 'chronicle_shelf', name: 'Chronicle Shelf', description: 'Storage for maps, relic records and field notes.', cost: 70, zone: 'interior', width: 2.5, depth: 1, solid: true, visual: { primitive: 'shelf', color: 0x6d503b, accent: 0xf0c477 } },
  { id: 'archive_alcove', name: 'Archive Alcove', description: 'A large modular study nook that reads as a small room.', cost: 140, zone: 'interior', width: 4, depth: 3, solid: true, visual: { primitive: 'divider', color: 0x34515f, accent: 0x62e5ff } },
  { id: 'relic_worktable', name: 'Relic Worktable', description: 'A display bench for half-understood discoveries.', cost: 95, zone: 'interior', width: 3, depth: 1.5, solid: true, visual: { primitive: 'table', color: 0x6f5944, accent: 0xffb35c } },
  { id: 'temporal_planter', name: 'Temporal Planter', description: 'A yard bed of plants gathered across the timeline.', cost: 65, zone: 'yard', width: 2.5, depth: 2.5, solid: true, visual: { primitive: 'planter', color: 0x496238, accent: 0x7bf0a1 } },
  { id: 'era_sundial', name: 'Era Sundial', description: 'A garden sculpture that never casts the same shadow twice.', cost: 110, zone: 'yard', width: 2, depth: 2, solid: true, visual: { primitive: 'statue', color: 0x60717a, accent: 0xffd36b } },
  { id: 'portal_lantern', name: 'Portal Lantern', description: 'A low beacon for the path through the yard.', cost: 55, zone: 'either', width: 1, depth: 1, solid: true, visual: { primitive: 'lamp', color: 0x394c58, accent: 0x62e5ff } },
] as const;

export const HOUSE_MODULE_BY_ID = new Map(HOUSE_MODULES.map((module) => [module.id, module]));

export function rotatedFootprint(module: HouseModuleDef, rotation: number): { width: number; depth: number } {
  const quarter = Math.round(rotation / (Math.PI / 2));
  return Math.abs(quarter) % 2 === 1 ? { width: module.depth, depth: module.width } : { width: module.width, depth: module.depth };
}

export function sanitizeHousePlacements(value: unknown): HousePlacement[] {
  if (!Array.isArray(value)) return [];
  const ids = new Set<string>();
  const out: HousePlacement[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const p = raw as Partial<HousePlacement>;
    if (typeof p.id !== 'string' || ids.has(p.id) || typeof p.moduleId !== 'string' || !HOUSE_MODULE_BY_ID.has(p.moduleId)) continue;
    if (!Number.isFinite(p.x) || !Number.isFinite(p.z) || !Number.isFinite(p.rotation)) continue;
    ids.add(p.id);
    out.push({ id: p.id, moduleId: p.moduleId, x: Number(p.x), z: Number(p.z), rotation: Number(p.rotation) });
  }
  return out.slice(0, 64);
}
